/**
 * Exact decimal arithmetic for money, sizes and prices.
 *
 * Every amount in this app is a decimal quantity on a grid (a market's step, a
 * token's decimals), and IEEE doubles cannot hold those exactly: `47.41 - 44.82`
 * is 2.5899999999999963, `0.01 * 57` is 0.5700000000000001. Rounding such a
 * value hands the trader a number they did not ask for — which is how a
 * 0.00000148 BTC balance once became 0.0000015 and was refused.
 *
 * Values travel as canonical decimal STRINGS (`Dec`) and only become numbers at
 * the edges: a slider position, a chart. Base units for a contract call are
 * `bigint`.
 *
 * This module is the ONLY place allowed to import `bignumber.js` (enforced by an
 * eslint `no-restricted-imports` rule), so the library can be swapped without
 * touching call sites.
 */
import BigNumber from "bignumber.js";

/** A finite decimal as plain text: no exponent, no separators, no leading "+". */
export type Dec = string;

/**
 * How a value that does not land on the grid is resolved.
 * `floor`/`ceil` go toward -∞/+∞; `down`/`up` go toward/away from zero. They
 * differ only for negative values.
 */
export type RoundMode = "floor" | "ceil" | "down" | "up";

// A private clone: configuring the shared BigNumber would leak into any
// dependency that also uses it. EXPONENTIAL_AT keeps `toFixed()` plain for every
// magnitude we can meet (a 1e-18 dust amount, a 1e21 wei figure).
const BN = BigNumber.clone({
  DECIMAL_PLACES: 40,
  ROUNDING_MODE: BigNumber.ROUND_DOWN,
  EXPONENTIAL_AT: 1e9,
  RANGE: 1e9,
});

const MODE: Record<RoundMode, BigNumber.RoundingMode> = {
  floor: BigNumber.ROUND_FLOOR,
  ceil: BigNumber.ROUND_CEIL,
  down: BigNumber.ROUND_DOWN,
  up: BigNumber.ROUND_UP,
};

// Beyond this a value is not an amount anyone holds, and expanding it would
// only burn memory (`"1e999999999"`).
const MAX_EXPONENT = 400;
const NUMERIC_TEXT = /^[+-]?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i;

/** Plain text for a finite BigNumber, trailing zeros trimmed ("1.10" -> "1.1"). */
const out = (value: BigNumber): Dec => value.toFixed();

const toBig = (value: Dec | number | bigint | null | undefined): BigNumber | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "bigint") return new BN(value.toString());
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    // A double carries ~15-17 significant digits; the digits past that are
    // representation noise, not intent. `toPrecision(15)` drops exactly those,
    // so 47.41 - 44.82 reads as 2.59 rather than 2.5899999999999963.
    return new BN(value.toPrecision(15));
  }
  const text = value.trim();
  if (!NUMERIC_TEXT.test(text)) return null;
  const exponent = Number(text.toLowerCase().split("e")[1] ?? 0);
  if (Math.abs(exponent) > MAX_EXPONENT) return null;
  const parsed = new BN(text);
  return parsed.isFinite() ? parsed : null;
};

/** `a` and `b` as BigNumbers, or null when either is not a number. */
const pair = (a: Dec | number, b: Dec | number): [BigNumber, BigNumber] | null => {
  const x = toBig(a);
  const y = toBig(b);
  return x && y ? [x, y] : null;
};

export const dec = {
  /** Canonical text for a value, or `null` when it is not a number. */
  parse(value: Dec | number | bigint | null | undefined): Dec | null {
    const parsed = toBig(value);
    return parsed ? out(parsed) : null;
  },

  add(a: Dec | number, b: Dec | number): Dec {
    const p = pair(a, b);
    return p ? out(p[0].plus(p[1])) : "0";
  },

  sub(a: Dec | number, b: Dec | number): Dec {
    const p = pair(a, b);
    return p ? out(p[0].minus(p[1])) : "0";
  },

  mul(a: Dec | number, b: Dec | number): Dec {
    const p = pair(a, b);
    return p ? out(p[0].times(p[1])) : "0";
  },

  /** `a / b` at `decimals` places. `null` when b is 0 or either is not a number. */
  div(a: Dec | number, b: Dec | number, decimals: number, mode: RoundMode): Dec | null {
    const p = pair(a, b);
    if (!p || p[1].isZero()) return null;
    return out(p[0].dividedBy(p[1]).decimalPlaces(decimals, MODE[mode]));
  },

  /** -1, 0 or 1. Values that are not numbers sort as 0 (equal). */
  cmp(a: Dec | number, b: Dec | number): -1 | 0 | 1 {
    const p = pair(a, b);
    return p ? (p[0].comparedTo(p[1]) as -1 | 0 | 1) : 0;
  },

  gt: (a: Dec | number, b: Dec | number): boolean => dec.cmp(a, b) > 0,
  gte: (a: Dec | number, b: Dec | number): boolean => dec.cmp(a, b) >= 0,
  lt: (a: Dec | number, b: Dec | number): boolean => dec.cmp(a, b) < 0,
  lte: (a: Dec | number, b: Dec | number): boolean => dec.cmp(a, b) <= 0,
  eq: (a: Dec | number, b: Dec | number): boolean => dec.cmp(a, b) === 0,

  isZero(value: Dec | number): boolean {
    const parsed = toBig(value);
    return parsed ? parsed.isZero() : false;
  },

  isNeg(value: Dec | number): boolean {
    const parsed = toBig(value);
    return parsed ? parsed.isNegative() && !parsed.isZero() : false;
  },

  min(...values: Array<Dec | number>): Dec {
    return values.reduce<Dec>(
      (lowest, value) => (dec.lt(value, lowest) ? dec.parse(value) ?? lowest : lowest),
      dec.parse(values[0]) ?? "0"
    );
  },

  max(...values: Array<Dec | number>): Dec {
    return values.reduce<Dec>(
      (highest, value) => (dec.gt(value, highest) ? dec.parse(value) ?? highest : highest),
      dec.parse(values[0]) ?? "0"
    );
  },

  round(value: Dec | number, decimals: number, mode: RoundMode): Dec {
    const parsed = toBig(value);
    return parsed ? out(parsed.decimalPlaces(Math.max(0, decimals), MODE[mode])) : "0";
  },

  /**
   * The nearest multiple of `step` in `mode`'s direction — the grid every market
   * quantity sits on. `null` when the step is not positive.
   */
  toStep(value: Dec | number, step: Dec | number, mode: RoundMode): Dec | null {
    const p = pair(value, step);
    if (!p || p[1].isLessThanOrEqualTo(0)) return null;
    const [amount, grid] = p;
    return out(amount.dividedBy(grid).integerValue(MODE[mode]).times(grid));
  },

  /** `value × bps / 10000` — slippage bounds and fee rates in integer basis points. */
  bps(value: Dec | number, basisPoints: number, mode: RoundMode): Dec {
    const parsed = toBig(value);
    if (!parsed || !Number.isFinite(basisPoints)) return "0";
    return out(
      parsed.times(basisPoints).dividedBy(10000).decimalPlaces(40, MODE[mode])
    );
  },

  /** Token base units, flooring anything past `decimals`. `null` if not a number. */
  toUnits(value: Dec | number, decimals: number, mode: RoundMode = "floor"): bigint | null {
    const parsed = toBig(value);
    if (!parsed) return null;
    const units = parsed.shiftedBy(decimals).integerValue(MODE[mode]);
    return BigInt(units.toFixed(0));
  },

  /** Base units back to a decimal amount. */
  fromUnits(units: bigint | string, decimals: number): Dec {
    const parsed = toBig(typeof units === "bigint" ? units : units.trim());
    return parsed ? out(parsed.shiftedBy(-decimals)) : "0";
  },

  /** Padded to exactly `decimals` places, for writing into a field or a label. */
  fixed(value: Dec | number, decimals: number, mode: RoundMode): string {
    const parsed = toBig(value) ?? new BN(0);
    return parsed.toFixed(Math.max(0, decimals), MODE[mode]);
  },

  /** UI only: a slider position, a chart point. Never a value headed on-chain. */
  toNumber(value: Dec | number): number {
    const parsed = toBig(value);
    return parsed ? parsed.toNumber() : 0;
  },
};
